import { BADGE_STAT_KEYS, type BadgeStatKey } from 'features/catalog/badge-definitions';
import type { CpuMood } from 'features/battle/ai';
import { FACE, FACE_CONCEDE_MULTIPLE, type CpuDifficulty } from 'features/battle/ai-config';
import { canDraw, hasTurnAction } from 'features/battle/queries';
import {
  battleFirstPile,
  battlePileCount,
  canActivateStat,
  liveBoardBadges,
  cardEffects,
  effectDoesNothing,
  effectRequiresDeck,
  emptyUnlockedPileCount,
  neighborActivatePrestige,
  nonemptyPileCount,
  opponentOf,
  prestigeIsShielded,
  prestigeToWin,
  scheduledActionsLeft,
  stateConditionHolds,
  totalStats,
} from 'features/battle/rules';
import {
  BATTLE_ACTIONS_PER_TURN,
  type BattleOp,
  type BattleState,
  type PileIndex,
  type PlayerId,
} from 'features/battle/types';

/** Faces sit on the CPU's own seat, not a person an effect handed the CPU. */
export const cpuShowsEmote = (state: Pick<BattleState, 'cpu'>, player: PlayerId): boolean => state.cpu === player;

/**
 * Whether the CPU face should be read again. Every settled action does, once the board has caught up.
 * `caughtUp` is false while a move is still stepping onto the board.
 */
export const cpuEmoteDue = (hasFace: boolean, settled: boolean, caughtUp: boolean): boolean => {
  if (!hasFace) return true;
  return settled && caughtUp;
};

/** Faces while the match is going. The first matching row in `CPU_FACE_BY_LEAD` picks one. */
export const CPU_EMOTE_LINES: Record<CpuMood, readonly string[]> = {
  scared: ['😱', '🫥', '💀', '🥶', '🫠'],
  uneasy: ['🫣'],
  steady: ['😌'],
  smug: ['😏'],
  taunting: ['😈', '😜', '🫵', '😂'],
};

/** Face before anyone has acted. */
export const CPU_OPENING_FACE: CpuMood = 'steady';

/** Faces once the match is over. */
export const CPU_END_LINES: Record<'win' | 'loss' | 'tie', readonly string[]> = {
  win: CPU_EMOTE_LINES.taunting,
  loss: ['😭', '💔', '👏', '😵'],
  tie: ['🤝', '🙃', '🤷'],
};

/** A feeling the face can settle on. Endings are their own feelings, separate from the moods during the match. */
export type EmoteFace = CpuMood | keyof typeof CPU_END_LINES;

export const EMOTE_FACE_LABELS: Record<EmoteFace, string> = {
  scared: 'Scared',
  uneasy: 'Uneasy',
  steady: 'Steady',
  smug: 'Smug',
  taunting: 'Taunting',
  win: 'Won',
  loss: 'Lost',
  tie: 'Tied',
};

const isEndedFace = (face: EmoteFace): face is keyof typeof CPU_END_LINES =>
  face === 'win' || face === 'loss' || face === 'tie';

/** Hover line for the CPU face. Live moods are "is Scared"; a finished match is "has Lost". */
export const cpuEmoteHover = (name: string, face: EmoteFace): string =>
  `${name} ${isEndedFace(face) ? 'has' : 'is'} ${EMOTE_FACE_LABELS[face]}`;

const emoteLines = (face: EmoteFace): readonly string[] =>
  face === 'win' || face === 'loss' || face === 'tie' ? CPU_END_LINES[face] : CPU_EMOTE_LINES[face];

/**
 * The emoji to show for `face`. The one already on screen stays while the feeling stays;
 * a new feeling picks again from that feeling's lines.
 */
export const keepCpuEmote = (
  face: EmoteFace,
  roll: number,
  shown: { face: EmoteFace; emoji: string } | null
): string => {
  if (shown?.face === face) return shown.emoji;
  const lines = emoteLines(face);
  return lines[Math.abs(roll) % lines.length];
};

/**
 * Resting face from how the match reads at the end of a turn. First matching row wins.
 * `score` is this seat's face score (`faceScore`). `held` means they were already ahead in Prestige
 * when the turn began, so a big read can taunt.
 */
export const CPU_FACE_BY_LEAD: readonly {
  scoreAtLeast?: number;
  scoreAtMost?: number;
  held?: boolean;
  face: CpuMood;
}[] = [
  { scoreAtLeast: 4, held: true, face: 'taunting' },
  { scoreAtLeast: 2.5, face: 'smug' },
  { scoreAtMost: -4, face: 'scared' },
  { scoreAtMost: -2.5, face: 'uneasy' },
  { face: 'steady' },
];

const scaredScoreAtMost = CPU_FACE_BY_LEAD.find((row) => row.face === 'scared')!.scoreAtMost!;

/** Face score at or below this makes that seat concede. Easy −7, Hard −8, Impossible −9. */
export const FACE_CONCEDE_AT: Record<CpuDifficulty, number> = {
  easy: scaredScoreAtMost * FACE_CONCEDE_MULTIPLE.easy,
  hard: scaredScoreAtMost * FACE_CONCEDE_MULTIPLE.hard,
  impossible: scaredScoreAtMost * FACE_CONCEDE_MULTIPLE.impossible,
};

/**
 * Actions `player` still gets. Reaching the Prestige target ends the match on the spot,
 * so a fast race to 10 leaves no leftover actions.
 */
const actionsLeftForSeat = (state: BattleState, player: PlayerId): number => {
  if (state.phase === 'game_over') return 0;
  return scheduledActionsLeft(state, player);
};

/** Actions still to be played. A fast race to 10 leaves none. */
const actionsStillToPlay = (state: BattleState): number => actionsLeftForSeat(state, 0) + actionsLeftForSeat(state, 1);

/** Biggest Prestige swing one leftover action can still produce (gain 4, or steal 2). */
const MAX_PRESTIGE_SWING_PER_ACTION = 4;

const prestigeFromAmount = (
  state: BattleState,
  actor: PlayerId,
  amount: number | 'nonempty_piles' | 'opponent_empty_unlocked_piles'
): number => {
  if (amount === 'nonempty_piles') return nonemptyPileCount(state.players[actor], state);
  if (amount === 'opponent_empty_unlocked_piles') {
    return emptyUnlockedPileCount(
      state.players[opponentOf(actor)],
      state.round,
      battlePileCount(state),
      battleFirstPile(state),
      state
    );
  }
  return amount;
};

/** Prestige this effect is sure to move. Branches that depend on a draw or a later reveal are skipped. */
const guaranteedPrestigeOps = (
  state: BattleState,
  actor: PlayerId,
  ops: readonly BattleOp[],
  pile: PileIndex | null
): { gain: number; steal: number } => {
  let gain = 0;
  let steal = 0;
  for (const op of ops) {
    if (op.kind === 'gain_prestige' && op.who !== 'opponent' && op.who !== 'all') {
      gain += prestigeFromAmount(state, actor, op.amount);
    } else if (op.kind === 'steal_prestige' && !prestigeIsShielded(state, opponentOf(actor))) {
      steal += op.amount;
    } else if (op.kind === 'if') {
      const holds = stateConditionHolds(state, actor, op.condition, pile);
      const branch = holds === true ? op.then : holds === false ? (op.otherwise ?? []) : null;
      if (branch) {
        const inner = guaranteedPrestigeOps(state, actor, branch, pile);
        gain += inner.gain;
        steal += inner.steal;
      }
    }
  }
  return { gain, steal };
};

/** Prestige this seat can cash with the actions it still has on this turn. */
const guaranteedCashThisTurn = (state: BattleState, player: PlayerId): { gain: number; steal: number } => {
  if (state.phase !== 'turn' || state.activePlayer !== player || state.actionsLeft <= 0) {
    return { gain: 0, steal: 0 };
  }
  const seat = state.players[player];
  const cashouts: { gain: number; steal: number }[] = [];
  for (const { cardId, pile } of liveBoardBadges(state, seat)) {
    for (const effect of cardEffects(state, cardId)) {
      if (
        effect.stat == null ||
        effect.trigger !== 'Activate' ||
        !canActivateStat(state, player, effect.stat) ||
        (effectRequiresDeck(effect.ops) && seat.deck.length === 0) ||
        effectDoesNothing(state, player, pile, effect.ops, true)
      ) {
        continue;
      }
      const cash = guaranteedPrestigeOps(state, player, effect.ops, pile);
      const gain = cash.gain + neighborActivatePrestige(state, player, pile);
      if (gain > 0 || cash.steal > 0) cashouts.push({ gain, steal: cash.steal });
    }
  }
  cashouts.sort((a, b) => b.gain + b.steal - (a.gain + a.steal));
  const picked = cashouts.slice(0, state.actionsLeft);
  let steal = 0;
  let gain = 0;
  const stealCap = state.players[opponentOf(player)].prestige;
  for (const cash of picked) {
    const taken = Math.min(cash.steal, Math.max(0, stealCap - steal));
    steal += taken;
    gain += cash.gain;
  }
  return { gain, steal };
};

const withPrestige = (state: BattleState, player: PlayerId, mine: number, theirs: number): BattleState => {
  const foe = opponentOf(player);
  const players = [...state.players] as BattleState['players'];
  players[player] = { ...state.players[player], prestige: mine };
  players[foe] = { ...state.players[foe], prestige: theirs };
  return { ...state, players };
};

/**
 * True when `player` has already won this board, or will on this turn: a cash that ends the match,
 * or a Prestige lead the other seat cannot close with the actions they have left.
 */
export const winLocked = (state: BattleState, player: PlayerId): boolean => {
  if (state.phase === 'game_over') return state.winner === player;
  const foe = opponentOf(player);
  const cash = guaranteedCashThisTurn(state, player);
  const mine = state.players[player].prestige + cash.gain;
  const theirs = Math.max(0, state.players[foe].prestige - cash.steal);
  if (mine <= theirs) return false;
  const foeLeft = actionsLeftForSeat(withPrestige(state, player, mine, theirs), foe);
  return foeLeft <= 0 || mine - theirs > foeLeft * MAX_PRESTIGE_SWING_PER_ACTION;
};

/**
 * Prestige weight. Last-go leftover actions count as a short clock. A leader close to the winning
 * total weighs more than the same gap earlier in the race.
 */
const prestigeWeight = (state: BattleState): number => {
  const needed = prestigeToWin(state);
  let weight = FACE.prestige;
  const remaining = actionsStillToPlay(state);
  if (Number.isFinite(remaining)) {
    const gone = Math.max(0, 28 - remaining);
    weight *= 1 + gone * FACE.prestigeWeightIncreasePerRemainingAction;
  }
  if (needed <= 0) return weight;
  const leader = Math.max(state.players[0].prestige, state.players[1].prestige);
  return weight * (1 + FACE.prestigeCloserToWin * (Math.min(leader, needed) / needed));
};

/**
 * Actions this seat is taking this turn, plus extra or skipped on their next.
 * Extra/skip already spent into this turn live in `actionsLeft` + `actionsTaken`, so a 4-action
 * turn still counts after the queue clears. Stays put while actions are spent, so the face does
 * not flicker mid-turn.
 */
const allottedActions = (state: BattleState, player: PlayerId): number => {
  const seat = state.players[player];
  const nextBonus = (seat.extraActions ?? 0) - seat.skipActions;
  if (state.activePlayer === player) {
    return Math.max(0, state.actionsLeft + (seat.actionsTaken ?? 0) + nextBonus);
  }
  return Math.max(0, BATTLE_ACTIONS_PER_TURN + nextBonus);
};

/**
 * Action hole or surplus is the main scale. Behind: deficits weigh more, leads weigh less.
 * Ahead: the reverse. Even board stays 0.
 */
const scaleByActionGap = (position: number, actionGap: number): number => {
  const scale = 1 + Math.abs(actionGap) * FACE.actionScalePerAction;
  if (scale === 1 || position === 0) return position;
  const ahead = actionGap >= 0;
  const winning = position >= 0;
  return ahead === winning ? position * scale : position / scale;
};

/** Stats this seat leads and can activate a board effect with. */
const activatableLeadStats = (state: BattleState, player: PlayerId): Set<BadgeStatKey> => {
  const stats = new Set<BadgeStatKey>();
  const seat = state.players[player];
  for (const { cardId, pile } of liveBoardBadges(state, seat)) {
    for (const effect of cardEffects(state, cardId)) {
      if (
        effect.stat != null &&
        effect.trigger === 'Activate' &&
        canActivateStat(state, player, effect.stat) &&
        (!effectRequiresDeck(effect.ops) || seat.deck.length > 0) &&
        !effectDoesNothing(state, player, pile, effect.ops, true)
      ) {
        stats.add(effect.stat);
      }
    }
  }
  return stats;
};

/**
 * This seat's read of the match, in Prestige-sized points. When both seats have the same number of
 * actions, the opponent's score is the negation. An action hole or surplus scales the rest of the
 * board, so the two faces are not mirrors then.
 */
export const faceScore = (state: BattleState, player: PlayerId): number => {
  const self = state.players[player];
  const foe = state.players[opponentOf(player)];
  const mine = totalStats(state, self);
  const theirs = totalStats(state, foe);
  const mineLive = activatableLeadStats(state, player);
  const foeLive = activatableLeadStats(state, opponentOf(player));
  let stats = 0;
  let leads = 0;
  for (const key of BADGE_STAT_KEYS) {
    stats += mine[key] - theirs[key];
    if (mineLive.has(key)) leads += 1;
    else if (foeLive.has(key)) leads -= 1;
  }
  const actionGap = allottedActions(state, player) - allottedActions(state, opponentOf(player));
  const position =
    (self.prestige - foe.prestige) * prestigeWeight(state) +
    (self.hand.length - foe.hand.length) * FACE.hand +
    (self.deck.length - foe.deck.length) * FACE.deck +
    stats * FACE.stats +
    leads * FACE.statLead;
  return scaleByActionGap(position, actionGap) + actionGap * FACE.tempo;
};

export const faceForLead = (score: number, held: boolean): CpuMood => {
  const row = CPU_FACE_BY_LEAD.find(
    (band) =>
      (band.scoreAtLeast == null || score >= band.scoreAtLeast) &&
      (band.scoreAtMost == null || score <= band.scoreAtMost) &&
      (!band.held || held)
  );
  return row?.face ?? 'steady';
};

/**
 * Resting face for a live match. A locked win taunts even when the lead was not held at the turn's start.
 */
export const cpuLiveFace = (state: BattleState, player: PlayerId, held: boolean): CpuMood =>
  winLocked(state, player) ? 'taunting' : faceForLead(faceScore(state, player), held);

/**
 * Behind on Prestige, nothing left to draw or reveal, and no board cash this turn.
 * The leftover legal activation would only help the other seat, so fold instead of taking it.
 */
const stuckBehindWithoutCash = (state: BattleState, player: PlayerId): boolean => {
  if (state.phase !== 'turn' || state.activePlayer !== player || state.actionsLeft <= 0) return false;
  if (state.players[player].prestige >= state.players[opponentOf(player)].prestige) return false;
  if (!hasTurnAction(state) || canDraw(state) || state.players[player].hand.length > 0) return false;
  const cash = guaranteedCashThisTurn(state, player);
  return cash.gain <= 0 && cash.steal <= 0;
};

/**
 * Seat that should fold this position. Only the CPU. A person always plays it out.
 * A board already at the winning total is ending, so leave it for the engine.
 * The face score is the read; Easy folds sooner on that same number, Impossible later.
 */
export const autoConcedeLoser = (state: BattleState): PlayerId | null => {
  if (state.phase === 'game_over' || state.pending || state.stack.length > 0) return null;
  if (state.players.some((seat) => seat.prestige >= prestigeToWin(state))) return null;
  const player = state.cpu;
  if (player == null) return null;
  if (winLocked(state, player)) return null;
  if (faceScore(state, player) <= FACE_CONCEDE_AT[state.cpuDifficulty ?? 'easy']) return player;
  if (stuckBehindWithoutCash(state, player)) return player;
  return null;
};
